import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { format } from "date-fns";
import { useClientWeeklyReports } from "../hooks/useClientWeeklyReports";

/**
 * Read-only weekly reports the client submitted — aligned with coach report summary sources.
 */
function WeeklyReportsPanel({ clientId, organizationId }) {
  const { reports, loading, error } = useClientWeeklyReports({ clientId, organizationId });

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 4 }}>
        <CircularProgress size={28} />
        <Typography color="text.secondary">Loading your weekly check-ins…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning">
        {error.message ?? "Could not load weekly check-ins."}
      </Alert>
    );
  }

  if (!reports.length) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          When you submit a weekly check-in from the mobile app, it will appear here with the same
          week range your coach sees in Reports.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <AssignmentTurnedInIcon color="primary" sx={{ fontSize: 28 }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color="primary">
            Weekly check-ins
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Submitted reports — coaches review these in Reports.
          </Typography>
        </Box>
      </Box>
      {reports.map((r) => {
        const start = r.startDate instanceof Date ? r.startDate : r.startDate ? new Date(r.startDate) : null;
        const end = r.endDate instanceof Date ? r.endDate : r.endDate ? new Date(r.endDate) : null;
        const range =
          start && end
            ? `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`
            : "Week";
        const submitted = r.createdOn instanceof Date ? r.createdOn : r.createdOn ? new Date(r.createdOn) : null;
        const healthN = Array.isArray(r.healthData) ? r.healthData.length : 0;
        const exN = Array.isArray(r.exerciseData) ? r.exerciseData.length : 0;
        const status = r.status === "processed" ? "Reviewed" : "Submitted";

        return (
          <Accordion
            key={r.id}
            elevation={0}
            sx={{
              mb: 1,
              border: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: "12px !important",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, width: "100%", pr: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {range}
                </Typography>
                <Chip size="small" label={status} color={r.status === "processed" ? "success" : "default"} />
                {submitted ? (
                  <Typography variant="caption" color="text.secondary">
                    Sent {format(submitted, "MMM d, yyyy")}
                  </Typography>
                ) : null}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" paragraph>
                This check-in included {healthN} daily health entr{healthN === 1 ? "y" : "ies"} and{" "}
                {exN} workout record{exN === 1 ? "" : "s"} in the report window.
              </Typography>
              {r.protocol?.name ? (
                <Typography variant="body2">
                  <strong>Plan:</strong> {r.protocol.name}
                </Typography>
              ) : null}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

export default WeeklyReportsPanel;
